import _ from 'lodash/fp'
import gql from 'graphql-tag'
import * as effects from 'redux-saga/effects'

import * as pinPositionsReducer from '../reducers/pinPositions'
import * as pinSetupsReducer from '../reducers/pinSetups'
import { apolloClient } from '@fe/business/src/apollo'

import { getCurrentClubId } from './network'

import * as snack from './snackMessage'

//  ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
//  ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
//  █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
//  ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
//  ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
//  ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝

const CLUB_PIN_SETUPS_QUERY = gql`
  query ClubPinSetups($input: ClubIdInput!) {
    PinPositionsQuery {
      getPinSetups(input: $input) {
        id
        name
        clubId
        courseId
        courseName
      }
    }
  }
`

const UPSERT_PIN_SETUP_MUTATION = gql`
  mutation UpsertPinSetup($input: UpsertPinSetupInput!) {
    PinPositionsMutation {
      upsertPinSetup(input: $input)
    }
  }
`

const DELETE_PIN_SETUP_MUTATION = gql`
  mutation DeletePinSetup($input: DeletePinSetupInput!) {
    PinPositionsMutation {
      deletePinSetup(input: $input)
    }
  }
`

function * loadPinSetupsEffect () {
  try {
    const clubId = yield effects.call(getCurrentClubId)
    const response = yield effects.call([apolloClient, apolloClient.query], {
      query: CLUB_PIN_SETUPS_QUERY,
      variables: { input: { clubId } },
      fetchPolicy: 'network-only'
    })
    return _.getOr([], 'data.PinPositionsQuery.getPinSetups', response)
  } catch (error) {
    return { error }
  }
}

function * upsertPinSetupEffect (pinSetup) {
  try {
    const clubId = parseInt(yield effects.call(getCurrentClubId))
    const response = yield effects.call([apolloClient, apolloClient.mutate], {
      mutation: UPSERT_PIN_SETUP_MUTATION,
      variables: {
        input: {
          pinSetupId: _.get('id', pinSetup) || null,
          name: _.get('name', pinSetup),
          courseId: _.get('courseId', pinSetup),
          clubId
        }
      }
    })

    return {
      ok: _.get('data.PinPositionsMutation.upsertPinSetup', response)
    }
  } catch (error) {
    return { error }
  }
}

function * deletePinSetupsEffect (pinSetupId) {
  try {
    const clubId = yield effects.call(getCurrentClubId)
    const response = yield effects.call([apolloClient, apolloClient.mutate], {
      mutation: DELETE_PIN_SETUP_MUTATION,
      variables: { input: { pinSetupId, clubId } }
    })
    return _.get('data.PinPositionsMutation.deletePinSetup', response)
  } catch (error) {
    return { error }
  }
}

//  ███████╗ █████╗  ██████╗  █████╗ ███████╗
//  ██╔════╝██╔══██╗██╔════╝ ██╔══██╗██╔════╝
//  ███████╗███████║██║  ███╗███████║███████╗
//  ╚════██║██╔══██║██║   ██║██╔══██║╚════██║
//  ███████║██║  ██║╚██████╔╝██║  ██║███████║
//  ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═╝╚══════╝

function * loadPinSetupsFlow () {
  const result = yield effects.call(loadPinSetupsEffect)
  if (result.error) {
    yield effects.put(pinSetupsReducer.actions.loadError(result.error))
    yield snack.genericError(result.error)
  } else {
    yield effects.all(_.map(p => effects.put(pinPositionsReducer.actions.loadRequest(p.id)), result))
    yield effects.put(pinSetupsReducer.actions.loadSuccess(result))
  }
}

function * upsertPinSetupFlow ({ payload }) {
  const result = yield effects.call(upsertPinSetupEffect, payload)
  if (result.error) {
    yield effects.put(pinSetupsReducer.actions.upsertError(result.error))
    yield snack.genericError(result.error)
  } else {
    yield effects.put(pinSetupsReducer.actions.loadRequest())
    yield effects.put(pinSetupsReducer.actions.upsertSuccess(payload))
  }
}

function * deletePinSetupFlow ({ payload }) {
  const result = yield effects.call(deletePinSetupsEffect, payload)
  if (result.error) {
    yield effects.put(pinSetupsReducer.actions.deleteError(result.error))
    yield snack.genericError(result.error)
  } else {
    yield effects.put(pinSetupsReducer.actions.loadRequest())
    yield effects.put(pinSetupsReducer.actions.deleteSuccess(payload))
  }
}

export default function * () {
  return yield effects.all([
    effects.takeLatest(pinSetupsReducer.types.LOAD_REQUEST, loadPinSetupsFlow),
    effects.takeLatest(pinSetupsReducer.types.UPSERT_REQUEST, upsertPinSetupFlow),
    effects.takeLatest(pinSetupsReducer.types.DELETE_REQUEST, deletePinSetupFlow)
  ])
}
